import { differenceInCalendarDays, parseISO } from 'date-fns';
import type { Pesaje, PuntoCurva, RegistroDiario } from './types';

export function dailySummary(
  fecha: string,
  lotes: number[] | undefined,
  registros: RegistroDiario[] | undefined,
  entregas: Array<{ fecha: string; kg: number }> | undefined,
) {
  const partes = registros?.filter((r) => r.fecha === fecha && lotes?.includes(r.lote_nave_id));
  const partesRegistrados = new Set(partes?.map((r) => r.lote_nave_id)).size;
  const completos = !!lotes?.length && partesRegistrados === lotes.length;
  const bajasParciales = partes
    ? partes.reduce((n, r) => n + r.bajas_muerte + r.bajas_triaje, 0)
    : null;
  return {
    bajas: completos ? bajasParciales : null,
    bajasParciales,
    partesRegistrados,
    completos,
    agua:
      completos && partes?.every((r) => r.agua_litros != null)
        ? partes.reduce((n, r) => n + r.agua_litros!, 0)
        : null,
    pienso: entregas?.filter((e) => e.fecha === fecha).reduce((n, e) => n + e.kg, 0) ?? null,
  };
}
export function sampleSummary(
  pesajes: Pesaje[],
  hasta: string,
  entrada: string,
  curva: PuntoCurva[],
) {
  const validos = pesajes.filter((p) => p.fecha <= hasta && p.num_pavos > 0);
  const fecha = validos.reduce((latest, p) => (p.fecha > latest ? p.fecha : latest), '');
  if (!fecha) return null;
  const muestras = validos.filter((p) => p.fecha === fecha);
  const aves = muestras.reduce((n, p) => n + p.num_pavos, 0);
  // Los gramos individuales evitan acumular el redondeo del peso medio de la API.
  const peso =
    muestras.reduce(
      (n, p) =>
        n +
        (p.pesos_g?.length === p.num_pavos
          ? p.pesos_g.reduce((sum, g) => sum + g, 0) / 1000
          : p.peso_medio_kg * p.num_pavos),
      0,
    ) / aves;
  const edad = differenceInCalendarDays(parseISO(fecha), parseISO(entrada));
  const esperado = expectedWeight(edad, curva);
  return {
    fecha,
    peso,
    aves,
    lotes: new Set(muestras.map((p) => p.lote_nave_id)).size,
    deltaGramos: esperado == null ? null : (peso - esperado) * 1000,
  };
}
export function expectedWeight(edad: number, curva: PuntoCurva[]): number | null {
  const puntos = [...curva].sort((a, b) => a.edad_dias - b.edad_dias);
  let esperado: number | null = puntos.find((p) => p.edad_dias === edad)?.peso_medio_kg ?? null;
  if (esperado == null) {
    const siguiente = puntos.findIndex((p) => p.edad_dias > edad);
    if (siguiente > 0) {
      const a = puntos[siguiente - 1],
        b = puntos[siguiente];
      esperado =
        a.peso_medio_kg +
        ((b.peso_medio_kg - a.peso_medio_kg) * (edad - a.edad_dias)) / (b.edad_dias - a.edad_dias);
    }
  }
  return esperado;
}
export function sensorAverage(readings: Array<{ value: number | null; state: string } | null>) {
  const validos = readings.filter(
    (r) =>
      r?.value != null && Number.isFinite(r.value) && (r.state === 'normal' || r.state === 'alert'),
  );
  return {
    value: validos.length ? validos.reduce((sum, r) => sum + r!.value!, 0) / validos.length : null,
    available: validos.length,
    total: readings.length,
  };
}
